import { useCallback, useMemo } from 'react'
import { FilterAlertType, WorkerAndTimeCards } from '../types'
import { WorkdaySendStatus } from 'types'
import { FilterWorkersWithVisibleItems } from 'components/buttons'
import { Anomalies } from 'components/buttons/AnomalousTimeCardButtons'

// Returns true if the provided AdminTimeCard
// matches any of the provided FilterAlertType,
// false otherwise.
const timeCardMatchesAnyAlertType = (
  timeCard: AdminTimeCard,
  types: Set<FilterAlertType>,
) => {
  if (types.has('issues') && timeCard.issues.length > 0) return true
  if (
    types.has('pending') &&
    timeCard.workdaySendStatus === WorkdaySendStatus.Pending
  )
    return true
  if (
    types.has('workdayError') &&
    timeCard.workdaySendStatus === WorkdaySendStatus.Error
  )
    return true

  return false
}

// Returns true if the provided AdminTimeCard
// matches any of the provided filtered anomalies,
// false otherwise.
const timeCardMatchesAnyAnomalyType = (
  timeCard: AdminTimeCard,
  types: Set<Anomalies>,
) => {
  if (types.has(Anomalies.HasTaggedTime) && timeCard.totalSecondsTagged > 0)
    return true
  if (types.has(Anomalies.NoBreak) && timeCard.totalBreaks === 0) return true
  if (
    types.has(Anomalies.HasTimeLoggedToUnassignedTasks) &&
    timeCard.hasTimeLoggedToUnassignedTasks
  )
    return true

  return false
}

// Returns true if the provided worker should be included
type WorkerFilterFn = (worker: TWorker) => boolean

// Returns true if the provided time card should be included
type TimeCardFilterFn = (timeCard: AdminTimeCard) => boolean

// Returns true if the provided worker and time cards should be included
// If false is returned, the entire worker is excluded
type WorkerAndTimeCardsFilterFn = (
  worker: TWorker,
  timeCards: AdminTimeCard[],
) => boolean

interface Options {
  hiddenDays: Set<number>
  filteredAlertTypes: Set<FilterAlertType>
  filteredAnomalyTypes: Set<Anomalies>
  hiddenStatuses: Set<Status>
  filterEmployeesWithVisibleTimeCards: FilterWorkersWithVisibleItems
  employeeFilter: {
    name: string
    type: ('salaried' | 'hourly' | 'unmanaged' | 'adminManaged')[]
  }
  filteredFacilityIds: Set<number>
  filteredApprovalGroupIds: Set<number>
}

interface FilterMap {
  // Filters that just care about the worker
  worker: {
    [key: string]: WorkerFilterFn
  }

  // Filters that just care about the time card
  timeCard: {
    [key: string]: TimeCardFilterFn
  }

  // Filters that care about the combined worker and time card collection
  workerAndTimeCards: {
    [key: string]: WorkerAndTimeCardsFilterFn
  }
}

export const useFiltering = ({
  hiddenDays,
  filteredAlertTypes,
  filteredAnomalyTypes,
  hiddenStatuses,
  filterEmployeesWithVisibleTimeCards,
  employeeFilter,
  filteredFacilityIds,
  filteredApprovalGroupIds,
}: Options) => {
  const dayFilter = useCallback<TimeCardFilterFn>(
    (timeCard: AdminTimeCard) => {
      if (hiddenDays.size === 0) return true

      return !hiddenDays.has(timeCard.date.weekday)
    },
    [hiddenDays],
  )

  const alertFilter = useCallback<TimeCardFilterFn>(
    (timeCard: AdminTimeCard) => {
      if (filteredAlertTypes.size === 0) return true

      return timeCardMatchesAnyAlertType(timeCard, filteredAlertTypes)
    },
    [filteredAlertTypes],
  )

  const anomalyFilter = useCallback<TimeCardFilterFn>(
    (timeCard: AdminTimeCard) => {
      if (filteredAnomalyTypes.size === 0) return true

      return timeCardMatchesAnyAnomalyType(timeCard, filteredAnomalyTypes)
    },
    [filteredAnomalyTypes],
  )

  const statusFilter = useCallback<TimeCardFilterFn>(
    (timeCard: AdminTimeCard) => {
      if (hiddenStatuses.size === 0) return true

      return !hiddenStatuses.has(timeCard.status)
    },
    [hiddenStatuses],
  )

  const visibleTimeCardFilter = useCallback<WorkerAndTimeCardsFilterFn>(
    (_worker: TWorker, timeCards: AdminTimeCard[]) => {
      return {
        all: true,
        atLeastOne: timeCards.length > 0,
        none: timeCards.length === 0,
      }[filterEmployeesWithVisibleTimeCards]
    },
    [filterEmployeesWithVisibleTimeCards],
  )

  const workerAttributeFilter = useCallback<WorkerFilterFn>(
    (worker: TWorker) => {
      const normalizedSearch = employeeFilter.name.trim().toLowerCase()

      const notFilteringNames = normalizedSearch === ''
      const notFilteringTypes = employeeFilter.type.length === 0
      const notFilteringFacilities = filteredFacilityIds.size === 0
      const notFilteringApprovalGroups = filteredApprovalGroupIds.size === 0

      // Optimization for when no filters are applied
      if (
        notFilteringNames &&
        notFilteringTypes &&
        notFilteringFacilities &&
        notFilteringApprovalGroups
      )
        return true

      return (
        (notFilteringNames ||
          worker.fullName.toLowerCase().includes(normalizedSearch)) &&
        (notFilteringTypes ||
          (employeeFilter.type.includes('unmanaged') &&
            worker.adminManagedGroup === null) ||
          (employeeFilter.type.includes('adminManaged') &&
            worker.adminManagedGroup !== null)) &&
        (notFilteringFacilities ||
          filteredFacilityIds.has(worker.facility.id)) &&
        (notFilteringApprovalGroups ||
          filteredApprovalGroupIds.has(worker.approvalGroup.id))
      )
    },
    [employeeFilter, filteredFacilityIds, filteredApprovalGroupIds],
  )

  const filters = useMemo<FilterMap>(
    () => ({
      worker: { workerAttributeFilter },
      timeCard: { dayFilter, alertFilter, anomalyFilter, statusFilter },
      workerAndTimeCards: { visibleTimeCardFilter },
    }),
    [
      dayFilter,
      alertFilter,
      anomalyFilter,
      statusFilter,
      visibleTimeCardFilter,
      workerAttributeFilter,
    ],
  )

  const applyFilters = useCallback(
    (workerAndTimeCards: WorkerAndTimeCards[], filters: FilterMap) =>
      workerAndTimeCards.reduce<WorkerAndTimeCards[]>(
        (acc, { worker, timeCards }) => {
          // Filter workers first, no need to proceed if the worker is excluded
          const includeWorker = Object.values(filters.worker).every((filter) =>
            filter(worker),
          )

          if (includeWorker) {
            // Filter Time Cards next
            const filteredTimeCards = timeCards.filter((timeCard) => {
              return Object.values(filters.timeCard).every((filter) =>
                filter(timeCard),
              )
            })

            // Determine if the worker should be included based on the new filtered Time Cards
            const includeWorkerAndTimeCards = Object.values(
              filters.workerAndTimeCards,
            ).every((filter) => filter(worker, filteredTimeCards))

            if (includeWorkerAndTimeCards) {
              acc.push({
                worker,
                timeCards: filteredTimeCards,
              })
            }
          }

          return acc
        },
        [],
      ),
    [],
  )

  // Given a filter key, return a FilterMap without that filter
  // We assume here that filter keys never clash, even across types
  // (i.e. a worker/timeCard/workerAndTimeCards filters never share a key)
  const filtersWithout = useCallback(
    (filterKey: string) => {
      const {
        worker: { [filterKey]: _excludedWorkersFilter, ...worker },
        timeCard: { [filterKey]: _excludedTimeCardsFilter, ...timeCard },
        workerAndTimeCards: {
          [filterKey]: _excludedWorkerAndTimeCardsFilter,
          ...workerAndTimeCards
        },
      } = filters

      return { worker, timeCard, workerAndTimeCards }
    },
    [filters],
  )

  return { filters, applyFilters, filtersWithout }
}
